import React from 'react'
import { Row, Col, Button, Card, CardHeader, CardTitle, CardBody } from 'reactstrap'
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ReactTable from '../../components/Lists/ReactTableWrapper'
import { withFirebase } from '../../components/Fb'
import FalconLoading from '../../components/Inputs/FalconLoading'
import PersonSearch from '../../components/Inputs/PersonSearch'
import PersonAndOrganisationSearch from '../../components/Inputs/PersonAndOrganisationSearch'
import TeamDeleteDialog from '../../components/Dialogs/TeamDeleteDialog'
import { describeFormat } from '../../components/utils/teamformats'
import {
  TEAM_STATES,
  describeManagers,
  entityDetails,
  entityName,
  managerEntry,
  managerIdsOf,
  payerLabel,
  payerModeLabel,
  teamQuota,
  teamStateLabel,
} from '../../components/utils/teams'

// Joukkueet (Teams / sarjaottelut). docs/features/teams.md §4.3 and Phase C.
//
// A team is the thing that holds a booking allowance and names the people who may spend it. The
// venue types ONE number - home ties - and the quota is derived from it (`homematches + 1`,
// plus any admin grant), because the spare tie exists so a manager can hold a date while an
// opponent decides.
//
// Not an rjsf/FalconModal screen, unlike Phases A and B. The two fields that matter most here
// are person and organisation pickers - PersonSearch and PersonAndOrganisationSearch - and
// neither can live inside a JSON Schema form without first writing a custom rjsf widget for it.
// A hand-built MUI dialog is less machinery than that widget would be, and it also lets the
// quota readout sit directly under the number it is derived from. docs/features/teams.md §10
// lists a schemas/team.js for this phase; it is deliberately not there.

const EMPTY_TEAM = {
  name: '',
  seasonid: '',
  formatid: '',
  organisationid: '',
  homematches: 5,
  quotaextra: 0,
  managers: [],
  payer: null,
  state: 'active',
}

// A picked person or organisation is shown with their EMAIL, not just their name. Both search
// components label their own chip `firstname + ' ' + lastname`, falling back to the
// organisation's name (PersonSearch.jsx:125, PersonAndOrganisationSearch.jsx:154), which in a
// venue holding two Matti Virtanens is not an identification at all - and the admin who granted
// booking rights, or pointed the invoices, at the wrong one has no way to see that they did. The
// identifying detail goes on screen the moment the pick is made rather than a page away in
// Henkilöt or Yritykset.
//
// Shared by the manager and the payer, which is why it takes an entity rather than a person:
// a payer may be an organisation, and then the business id leads (see entityDetails).
const EntityCard = ({ entity, onRemove, removeLabel, disabled }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      p: 1.5,
    }}
  >
    <Box>
      <Typography variant='body2'>
        <b>{entityName(entity)}</b>
      </Typography>
      {entityDetails(entity).map((line) => (
        <Typography key={line} variant='body2' color='text.secondary'>
          {line}
        </Typography>
      ))}
    </Box>
    <Button color='link' onClick={onRemove} disabled={disabled}>
      {removeLabel}
    </Button>
  </Box>
)

// The quota is derived, so the editor shows the arithmetic rather than a bare total: a venue
// that types 5 and reads 6 needs to see where the sixth came from, or it looks like a bug.
const QuotaReadout = ({ team }) => {
  const quota = teamQuota(team)
  if (!quota) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Anna kotiotteluiden määrä, niin varausoikeus lasketaan.
      </Typography>
    )
  }
  const extra = team.quotaextra > 0 ? ' + ' + team.quotaextra + ' lisäoikeus' : ''
  return (
    <Typography variant='body2'>
      <b>Varausoikeus: {quota} kohtaamista</b> ({team.homematches} kotiottelua + 1 vara
      {extra})
      <Typography component='span' variant='caption' color='text.secondary' sx={{ display: 'block' }}>
        Vara on se kohtaaminen, jonka joukkueenjohtaja peruu kun vastustajat ovat vahvistaneet
        loput. Sitä ei tarvitse merkitä erikseen.
      </Typography>
    </Typography>
  )
}

class TeamsContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      loaderror: null,
      teams: [],
      seasons: [],
      formats: [],
      seasonfilter: 'all',
      editing: null,
      saving: false,
      saveerror: null,
      confirmDelete: null,
      deleting: false,
    }
  }

  componentDidMount() {
    this.loadData()
  }

  loadData = async () => {
    try {
      const [teamSnap, seasonSnap, formatSnap, organisationSnap] = await Promise.all([
        this.props.firebase.getTeams(),
        this.props.firebase.getTeamSeasons(),
        this.props.firebase.getTeamFormats(),
        this.props.firebase.listenOrganisations().get(),
      ])
      // Filtered client side, NOT with .where('state','!=','deleted'): a != query drops every
      // document that has no state field at all. Same reasoning as getRecurringInquiries.
      const alive = (snap) =>
        snap.empty ? [] : snap.docs.map((d) => ({ ...d.data(), id: d.id })).filter((x) => x.state !== 'deleted')
      const teams = alive(teamSnap).sort((a, b) => (a.name || '').localeCompare(b.name || ''))

      // The payer picker searches persons through Algolia but organisations through
      // firebase.searchCombined, which reads a Fuse index that is only ever built by
      // ReservationModal (:308 - the ONLY caller of updateCombinedCache). Open this screen
      // without having opened a reservation first and the organisation half of the picker
      // silently returns nothing: no error, just persons. So this screen fills the index
      // itself, the same way ReservationModal does.
      //
      // AffiliatePartnerEditor.jsx:63 hit the same wall and answered it by avoiding the shared
      // picker altogether. Priming the cache is the cheaper half of that trade: it costs one
      // read and keeps person search, which a payer picker needs anyway.
      this.props.firebase.updateCombinedCache(alive(organisationSnap), null)

      this.setState({
        teams,
        seasons: alive(seasonSnap),
        formats: alive(formatSnap),
        loading: false,
        loaderror: null,
      })
    } catch (error) {
      console.error('Loading teams failed', error)
      this.setState({ loading: false, loaderror: error.message })
    }
  }

  seasonById = (id) => this.state.seasons.find((s) => s.id === id) || null

  formatById = (id) => this.state.formats.find((f) => f.id === id) || null

  // The formats a team on this season may actually book. A season carries its own allow-list
  // (§4.1), so offering every format in the venue here would let an admin point a team at one
  // the season forbids - the team would look configured and the Phase E guard would refuse
  // every tie. An empty allow-list on the season means "no restriction".
  formatsForSeason = (seasonid) => {
    const season = this.seasonById(seasonid)
    const allowed = season && Array.isArray(season.formats) ? season.formats : []
    if (!allowed.length) return this.state.formats
    return this.state.formats.filter((f) => allowed.includes(f.id))
  }

  visibleTeams = () => {
    if (this.state.seasonfilter === 'all') return this.state.teams
    return this.state.teams.filter((t) => t.seasonid === this.state.seasonfilter)
  }

  // Derived rows, not the documents: the editor writes back the object it was opened with, so a
  // summary hung on a team would be saved to Firestore as a field nothing reads. Same reason as
  // TeamFormatsContainer.tableRows().
  tableRows = () =>
    this.visibleTeams().map((t) => {
      const season = this.seasonById(t.seasonid)
      const format = this.formatById(t.formatid)
      return {
        id: t.id,
        name: t.name,
        seasonname: season ? season.name || '(nimetön kausi)' : '(kausi poistettu)',
        formatname: format ? format.name || '(nimetön)' : '(ottelumuoto poistettu)',
        quota: teamQuota(t),
        managers: describeManagers(t.managers),
        payer: payerLabel(t, season),
        statetext: teamStateLabel(t.state),
      }
    })

  addNewTeam = () => {
    // Pre-selects the season being filtered on, which is the season the admin is looking at.
    const seasonid = this.state.seasonfilter === 'all' ? '' : this.state.seasonfilter
    this.setState({ editing: { ...EMPTY_TEAM, seasonid }, saveerror: null })
  }

  handleRowSelect = (id) => {
    const team = this.state.teams.find((t) => t.id === id)
    if (!team) return
    // Edited on a copy. The row the table renders comes from state.teams, and mutating the
    // document in place would repaint the list with unsaved edits as though they were saved.
    this.setState({ editing: { ...EMPTY_TEAM, ...team }, saveerror: null })
  }

  closeEditor = () => {
    this.setState({ editing: null, saveerror: null })
  }

  setField = (field, value) => {
    this.setState({ editing: { ...this.state.editing, [field]: value } })
  }

  // Changing the season may invalidate the format, because each season allows its own set. The
  // stale id has to be cleared here rather than validated at save time: left in place it looks
  // to the admin like a configured team, and Phase A already learned this lesson the expensive
  // way - a season field that kept its old value silently widened the next slot's court list.
  handleSeasonChange = (seasonid) => {
    const stillAllowed = this.formatsForSeason(seasonid).some((f) => f.id === this.state.editing.formatid)
    this.setState({
      editing: {
        ...this.state.editing,
        seasonid,
        formatid: stillAllowed ? this.state.editing.formatid : '',
      },
    })
  }

  // PersonSearch hands back (name, values) where values are search hits. Only the five fields a
  // venue needs are kept - see managerEntry().
  //
  // ONE manager for now, though `managers` stays an array: §4.3 has always been plural and one
  // person managing several teams is already specified, so capping the UI costs nothing to undo
  // later while a second manager today would ship a rights model nobody has asked for yet. The
  // cap is enforced here and by not rendering the search once a manager is set - PersonSearch's
  // own `onlyone` does not actually block a second add (PersonSearch.jsx:75 tests length > 1,
  // so it lets the second one through and only trims on the next props round trip).
  handleManagerChange = (name, values) => {
    const picked = (values || []).filter((v) => v && v.id)
    this.setField('managers', picked.slice(-1).map(managerEntry))
  }

  removeManager = () => {
    this.setField('managers', [])
  }

  // Back to null, not to an empty object: null is what §4.3 means by "inherit the season's
  // payerdefault", and an empty object would be a payer with no identity.
  removePayer = () => {
    this.setField('payer', null)
  }

  // One payer or none. The picker is a multi-select shared with ReservationModal, so the extra
  // ones are dropped here rather than by configuring it away - a team pays as one thing, and
  // `persons.payers` shares (§4.5) are a reservation-level concern.
  handlePayerChange = (name, values) => {
    const picked = (values || []).filter((v) => v && v.id)
    this.setField('payer', picked.length ? picked[picked.length - 1] : null)
  }

  canSave = () => {
    const t = this.state.editing
    if (!t) return false
    return !!(t.name && t.name.trim() && t.seasonid && t.formatid && teamQuota(t) > 0)
  }

  saveTeam = async () => {
    const team = this.state.editing
    if (!this.canSave()) return
    this.setState({ saving: true, saveerror: null })
    try {
      const before = team.id ? this.state.teams.find((t) => t.id === team.id) : null
      const known = managerIdsOf(before && before.managers)

      if (team.id) await this.props.firebase.updateTeam(team)
      else await this.props.firebase.createTeam(team)

      // Only newly added managers are granted. Re-granting on every save would be harmless but
      // noisy - each call is a Cloud Function round trip - and the grant is not per team.
      const added = managerIdsOf(team.managers).filter((id) => !known.includes(id))
      const results = await Promise.all(added.map((id) => this.props.firebase.setTeamManagerAcl(id)))

      // The team document is already written at this point. A failed grant must therefore be
      // reported rather than thrown away: the venue would otherwise see a saved team whose
      // manager cannot sign in, with nothing on screen to explain why.
      const failed = results.filter((r) => r && r.error)
      await this.loadData()
      if (failed.length) {
        this.setState({
          saving: false,
          saveerror:
            'Joukkue tallennettiin, mutta ' +
            failed.length +
            ' joukkueenjohtajan käyttöoikeuden anto epäonnistui. Avaa joukkue ja tallenna uudelleen.',
        })
        return
      }
      this.setState({ saving: false, editing: null })
    } catch (error) {
      console.error('Saving team failed', error)
      this.setState({ saving: false, saveerror: error.message })
    }
  }

  askDelete = (team) => {
    this.setState({ confirmDelete: team })
  }

  cancelDelete = () => {
    this.setState({ confirmDelete: null })
  }

  confirmDelete = async () => {
    const team = this.state.confirmDelete
    if (!team) return
    this.setState({ deleting: true })
    try {
      await this.props.firebase.deleteTeam(team.id)
      await this.loadData()
      this.setState({ confirmDelete: null, deleting: false, editing: null })
    } catch (error) {
      console.error('Deleting team failed', error)
      this.setState({ deleting: false })
    }
  }

  renderEditor = () => {
    const team = this.state.editing
    if (!team) return null
    const formats = this.formatsForSeason(team.seasonid)
    const season = this.seasonById(team.seasonid)
    const managers = Array.isArray(team.managers) ? team.managers : []
    return (
      <Dialog open={!!team} onClose={this.closeEditor} maxWidth='md' fullWidth>
        <DialogTitle>{team.id ? 'Muokkaa joukkuetta' : 'Uusi joukkue'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label='Joukkueen nimi'
              placeholder='esim. HVS Miehet 1'
              value={team.name || ''}
              onChange={(e) => this.setField('name', e.target.value)}
              variant='standard'
              fullWidth
              required
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl variant='standard' fullWidth required>
                <InputLabel>Kausi</InputLabel>
                <Select value={team.seasonid || ''} onChange={(e) => this.handleSeasonChange(e.target.value)}>
                  {this.state.seasons.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {(s.name || '(nimetön)') + (s.active ? '' : ' (ei käytössä)')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant='standard' fullWidth required disabled={!team.seasonid}>
                <InputLabel>Ottelumuoto</InputLabel>
                <Select value={team.formatid || ''} onChange={(e) => this.setField('formatid', e.target.value)}>
                  {formats.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name || '(nimetön)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {team.formatid && this.formatById(team.formatid) ? (
              <Typography variant='caption' color='text.secondary'>
                {describeFormat(this.formatById(team.formatid))}
              </Typography>
            ) : null}
            {team.seasonid && !formats.length ? (
              <Alert severity='warning'>
                Kaudelle ei ole sallittu yhtään ottelumuotoa. Lisää ne kauden asetuksissa, muuten
                joukkueenjohtaja ei voi varata mitään.
              </Alert>
            ) : null}

            <Divider />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label='Kotiottelut'
                type='number'
                value={team.homematches === undefined || team.homematches === null ? '' : team.homematches}
                onChange={(e) => this.setField('homematches', e.target.value === '' ? null : Number(e.target.value))}
                variant='standard'
                required
                sx={{ minWidth: 160 }}
              />
              <TextField
                label='Lisäoikeus'
                type='number'
                value={team.quotaextra === undefined || team.quotaextra === null ? '' : team.quotaextra}
                onChange={(e) => this.setField('quotaextra', e.target.value === '' ? 0 : Number(e.target.value))}
                variant='standard'
                helperText='Normaalisti 0.'
                sx={{ minWidth: 160 }}
              />
            </Stack>
            <QuotaReadout team={team} />

            <Divider />

            <Box>
              <Typography variant='body2' gutterBottom>
                Joukkueenjohtaja
              </Typography>
              {managers.length ? (
                <EntityCard
                  entity={managers[0]}
                  onRemove={this.removeManager}
                  removeLabel='Vaihda'
                  disabled={this.state.saving}
                />
              ) : (
                <PersonSearch
                  variant='list'
                  onChange={this.handleManagerChange}
                  values={null}
                  label=' '
                  placeholder='Etsi joukkueenjohtaja nimellä tai sähköpostilla'
                  fullWidth
                />
              )}
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
                Joukkueenjohtaja saa oikeuden varata tämän joukkueen kohtaamiset ilman
                ylläpito-oikeuksia. Tarkista sähköposti — samannimisiä henkilöitä voi olla
                useita. Sama henkilö voi johtaa useaa joukkuetta.
              </Typography>
            </Box>

            <Box>
              <Typography variant='body2' gutterBottom>
                Maksaja (valinnainen)
              </Typography>
              {team.payer ? (
                <EntityCard
                  entity={team.payer}
                  onRemove={this.removePayer}
                  removeLabel='Poista'
                  disabled={this.state.saving}
                />
              ) : (
                <PersonAndOrganisationSearch
                  onChange={this.handlePayerChange}
                  values={[]}
                  label=' '
                  placeholder='Etsi henkilö tai yritys'
                />
              )}
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
                {team.payer
                  ? 'Tämä maksaja ohittaa kauden oletuksen. Poista, niin kausi päättää.'
                  : payerModeLabel(season && season.payerdefault)
                  ? 'Tyhjä = kauden oletus (' + payerModeLabel(season.payerdefault) + ').'
                  : 'Tyhjä = kauden oletus.'}
              </Typography>
            </Box>

            <FormControl variant='standard' sx={{ minWidth: 200 }}>
              <InputLabel>Tila</InputLabel>
              <Select value={team.state || 'active'} onChange={(e) => this.setField('state', e.target.value)}>
                {TEAM_STATES.map((s) => (
                  <MenuItem key={s.const} value={s.const}>
                    {s.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {this.state.saveerror ? <Alert severity='error'>{this.state.saveerror}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {team.id ? (
              <Button color='danger' onClick={() => this.askDelete(team)} disabled={this.state.saving}>
                Poista
              </Button>
            ) : null}
          </Box>
          <Box>
            <Button onClick={this.closeEditor} disabled={this.state.saving}>
              Peruuta
            </Button>
            <Button color='success' onClick={this.saveTeam} disabled={!this.canSave() || this.state.saving}>
              {this.state.saving ? 'Tallennetaan...' : 'Tallenna'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    )
  }

  render() {
    if (this.state.loading) {
      return <FalconLoading />
    }
    return (
      <>
        {/* .content supplies the top margin that clears the fixed navbar
            (_sidebar-and-main-panel.scss:522). */}
        <div className='content'>
          <Card>
            <CardHeader>
              <Row>
                <Col lg='10' md='5' sm='5'>
                  <CardTitle tag='h5'>Joukkueet</CardTitle>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {this.state.loaderror ? (
                <Row>
                  <Col>
                    <Alert severity='error' sx={{ mb: 2 }}>
                      Joukkueiden lataus epäonnistui: {this.state.loaderror}
                    </Alert>
                  </Col>
                </Row>
              ) : null}
              {!this.state.seasons.length ? (
                <Row>
                  <Col>
                    <Alert severity='info' sx={{ mb: 2 }}>
                      Luo ensin kausi. Joukkue varaa aina jonkin kauden kalenterista, joten
                      joukkuetta ei voi tehdä ennen sitä.
                    </Alert>
                  </Col>
                </Row>
              ) : null}
              <Row className='align-items-center'>
                <Col md='4'>
                  <Button onClick={this.addNewTeam} disabled={!this.state.seasons.length}>
                    Lisää joukkue
                  </Button>
                </Col>
                <Col md='4'>
                  <FormControl variant='standard' fullWidth>
                    <InputLabel>Kausi</InputLabel>
                    <Select
                      value={this.state.seasonfilter}
                      onChange={(e) => this.setState({ seasonfilter: e.target.value })}
                    >
                      <MenuItem value='all'>Kaikki kaudet</MenuItem>
                      {this.state.seasons.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name || '(nimetön)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>
              </Row>
              <Row>
                <Col md='12'>
                  <ReactTable
                    data={this.tableRows()}
                    noSumRow={true}
                    columns={[
                      { Header: 'Joukkue', accessor: 'name' },
                      { Header: 'Kausi', accessor: 'seasonname' },
                      { Header: 'Ottelumuoto', accessor: 'formatname' },
                      { Header: 'Varausoikeus', accessor: 'quota', width: 130 },
                      { Header: 'Joukkueenjohtaja', accessor: 'managers' },
                      { Header: 'Maksaja', accessor: 'payer' },
                      { Header: 'Tila', accessor: 'statetext', width: 110 },
                    ]}
                    onRowClick={(params) => this.handleRowSelect(params.row.id)}
                  />
                </Col>
              </Row>
              <Typography variant='caption' color='text.secondary'>
                Vahvistettujen kohtaamisten määrä näkyy tässä listassa kun kohtaamiset on
                toteutettu (Phase D).
              </Typography>
            </CardBody>
          </Card>
          {this.renderEditor()}
          <TeamDeleteDialog
            open={!!this.state.confirmDelete}
            name={this.state.confirmDelete ? this.state.confirmDelete.name : ''}
            working={this.state.deleting}
            onConfirm={this.confirmDelete}
            onCancel={this.cancelDelete}
          />
        </div>
      </>
    )
  }
}

export default withFirebase(TeamsContainer)
